import { ExternalPdfFieldType, FieldTypes, InputSize } from '@hb/shared'
import { CSSProperties } from 'react'

// Per-field-type styling for PDF template attachment inputs.
//
// Template layouts are authored against a fixed on-screen appearance of each field. The global
// input styling was recently reworked to a `size`-token system whose default ('md') renders field
// text at 14px (previously every field rendered at a fixed 12px `hero-new`). Left unpinned,
// attachment inputs pick up that new default and shift inside the fixed-size boxes template
// authors laid out — forcing every existing template to be re-adjusted.
//
// To keep attachments immune to global field-style changes, each attachment input pins its own
// `size` (decoupling it from whatever the surrounding form context resolves to) and, for the plain
// text types, freezes the input text to the pre-redesign appearance. INITIALS and SIGNATURE render
// their own bespoke fonts (e.g. the Dancing Script cursive), so they only pin `size` and must NOT
// receive a font-family/size override.

export interface AttachmentFieldStyle {
  /** Input size the attachment input is pinned to, regardless of the surrounding form context. */
  size: InputSize
  /** Frozen overrides applied to the input's text element (only reaches text-based inputs). */
  inputStyle?: CSSProperties
  /** Container-level overrides (e.g. padding) merged over the default attachment padding. */
  style?: CSSProperties
}

// Default padding applied to every attachment input container (per-field-type `style` overrides it).
export const defaultAttachmentContainerStyle: CSSProperties = {
  padding: '0.5rem 0.35rem',
}

// Radio attachments store an editable font size (rem) that maps to one of the discrete input sizes,
// which drives both the radio control size and the option text. These rem values mirror the
// `fontSize` of each INPUT_SIZE_TOKENS entry (see forms/Input/Shared/sizes.ts).
export const DEFAULT_RADIO_FONT_SIZE = 0.875 // 'md'
const RADIO_FONT_SIZE_BY_INPUT_SIZE: [InputSize, number][] = [
  ['xs', 0.6875],
  ['sm', 0.75],
  ['md', 0.875],
  ['lg', 1],
]

// Map an editable font size (rem) to the nearest discrete input size.
export const inputSizeFromFontSize = (fontSize?: number): InputSize => {
  const target = fontSize ?? DEFAULT_RADIO_FONT_SIZE
  return RADIO_FONT_SIZE_BY_INPUT_SIZE.reduce((best, curr) =>
    Math.abs(curr[1] - target) < Math.abs(best[1] - target) ? curr : best,
  )[0]
}

// The pre-redesign field text appearance (old `baseInputStyle` for theme='detailed').
const frozenTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  lineHeight: 'normal',
  fontFamily: 'hero-new',
}

// Initials render their own cursive (Dancing Script) font, so we keep `fontFamily` untouched. They
// carry only a few characters, so they get a larger font than the plain text fields; the reduced
// container padding below keeps the overall height matched to the date field. `lineHeight` is left
// at normal (as the date field) so the two share the same line-box behavior.
const initialsInputStyle: CSSProperties = {
  fontSize: '0.875rem',
  lineHeight: 'normal',
}

// Trimmed vertical padding to offset the larger initials font, so the box height still matches the
// date field (~0.42rem vs the default 0.5rem).
const initialsContainerStyle: CSSProperties = {
  padding: '0.42rem 0.35rem',
}

export const attachmentFieldStyles: Record<ExternalPdfFieldType, AttachmentFieldStyle> = {
  [FieldTypes.TEXT]: { size: 'md', inputStyle: frozenTextStyle },
  [FieldTypes.TEXTAREA]: { size: 'md', inputStyle: frozenTextStyle },
  [FieldTypes.DATE]: { size: 'md', inputStyle: frozenTextStyle },
  // Bespoke cursive font — bigger text + trimmed padding, matched in height to the date field.
  [FieldTypes.INITIALS]: {
    size: 'md',
    inputStyle: initialsInputStyle,
    style: initialsContainerStyle,
  },
  // Multi-input widget — pin size only, leave the input font untouched.
  [FieldTypes.SIGNATURE]: { size: 'md' },
  [FieldTypes.FILE]: { size: 'md' },
  // Radio option group — pin size only (no single-line text overrides).
  [FieldTypes.DROPDOWN]: { size: 'md' },
}

export const getAttachmentFieldStyle = (type: FieldTypes): AttachmentFieldStyle =>
  attachmentFieldStyles[type as ExternalPdfFieldType] ?? { size: 'md', inputStyle: frozenTextStyle }
